import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import React, { useState } from 'react';
import styled from 'styled-components';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import { useGetEpdQRCode } from 'services/api/queries';
import { LibraryQRCodeFormat, LibraryQRCodeLogotypeType, Option } from 'types/types';
import { HyperLink } from 'styles/Styles.styled';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import { AxiosResponseHeaders } from 'axios';

const logotypeTypeOptions = [
  {
    label: 'Primary traceability logotype',
    value: LibraryQRCodeLogotypeType.PRIMARY,
  },
  {
    label: 'Secondary traceability logotype',
    value: LibraryQRCodeLogotypeType.SECONDARY,
  },
];

const primarySizeOptions = [
  {
    label: 'Web size coloured (820 x 1100 px, 96 ppi)',
    value: LibraryQRCodeFormat.WEB_SIZE_COLOURED,
  },
  {
    label: 'Web size grayscale (820 x 1100 px, 96 ppi)',
    value: LibraryQRCodeFormat.WEB_SIZE_GRAYSCALE,
  },
  {
    label: 'Print size coloured (1040 x 1340 px, 300 dpi)',
    value: LibraryQRCodeFormat.PRINT_SIZE_COLOURED,
  },
  {
    label: 'Print size grayscale (1040 x 1340 px, 300 dpi)',
    value: LibraryQRCodeFormat.PRINT_SIZE_GRAYSCALE,
  },
];

const secondarySizeOptions = [
  {
    label: 'Web size coloured (820 x 285 px, 96 ppi)',
    value: LibraryQRCodeFormat.WEB_SIZE_COLOURED,
  },
  {
    label: 'Web size grayscale (820 x 285 px, 96 ppi)',
    value: LibraryQRCodeFormat.WEB_SIZE_GRAYSCALE,
  },
  {
    label: 'Print size coloured (1040 x 362 px, 300 dpi)',
    value: LibraryQRCodeFormat.PRINT_SIZE_COLOURED,
  },
  {
    label: 'Print size grayscale (1040 x 362 px, 300 dpi)',
    value: LibraryQRCodeFormat.PRINT_SIZE_GRAYSCALE,
  },
];

const getSizeOptions = (logotypeType: Option | null) => {
  switch (logotypeType?.value) {
    case LibraryQRCodeLogotypeType.PRIMARY:
      return primarySizeOptions;
    case LibraryQRCodeLogotypeType.SECONDARY:
      return secondarySizeOptions;
    default:
      return [];
  }
};

interface Props {
  epdId: string;
  friendlyUrl: string | undefined;
  registrationNumber: string | undefined;
  disabled: boolean;
}

const QRCodeDownloadDialog: React.FunctionComponent<Props> = ({ epdId, friendlyUrl, registrationNumber, disabled }) => {
  const [isQRDownloadDialogOpen, setIsQRDownloadDialogOpen] = useState(false);

  const [logotypeType, setLogotypeType] = useState<Option | null>(null);
  const [qrCodeFormat, setQRCodeFormat] = useState<Option | null>(null);

  const { refetch, isFetching } = useGetEpdQRCode(
    epdId,
    friendlyUrl!,
    qrCodeFormat?.value as LibraryQRCodeFormat,
    logotypeType?.value as LibraryQRCodeLogotypeType,
    registrationNumber!
  );

  const handleLogotypeTypeChange = (option: Option) => {
    setLogotypeType(option);
    setQRCodeFormat(null);
  };

  const handleSizeChange = (option: Option) => {
    setQRCodeFormat(option);
  };

  const handleCloseDialog = () => {
    setLogotypeType(null);
    setQRCodeFormat(null);
    setIsQRDownloadDialogOpen(false);
  };

  const handleDownload = async () => {
    try {
      const { data } = await refetch();
      const blob = data?.data;
      const headers = data?.headers as AxiosResponseHeaders;

      if (blob) {
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = getFileNameFromHeaders(headers) ?? `${friendlyUrl}_qr.png`;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
    } finally {
      handleCloseDialog();
    }
  };

  const footerContent = (
    <div>
      <SecondaryButton label="Cancel" onClick={handleCloseDialog} outlined />
      <PrimaryButton label="Download" onClick={handleDownload} disabled={!qrCodeFormat || isFetching} autoFocus />
    </div>
  );

  return (
    <>
      <QRCodeButtonBox>
        <Button
          style={{ fontSize: 14, fontWeight: 500 }}
          type="button"
          label="+ Generate traceability logotype"
          text
          onClick={() => setIsQRDownloadDialogOpen(true)}
          disabled={disabled}
        />
      </QRCodeButtonBox>
      <StyledDialog
        header={'Download traceability logotype'}
        visible={isQRDownloadDialogOpen}
        style={{ width: 560 }}
        footer={footerContent}
        onHide={() => setIsQRDownloadDialogOpen(false)}
      >
        <DialogContentContainer>
          <InformationContainer style={{ marginTop: 0, fontSize: 14 }}>
            Select <strong>Primary</strong> or <strong>Secondary</strong> traceability logotype to proceed. Find more
            information about the logotypes{' '}
            <HyperLink href="https://www.environdec.com/resources/brand-assets">here</HyperLink>.
          </InformationContainer>
          <CompilerSelectControlledComponent
            containerStyle={{ marginTop: 30 }}
            label="Logotype type"
            options={logotypeTypeOptions}
            name="logotypeType"
            value={logotypeType}
            onChange={handleLogotypeTypeChange}
            placeholder="Select"
            isClearable={false}
            required={!logotypeType}
          />
          <CompilerSelectControlledComponent
            containerStyle={{ marginTop: 30 }}
            label="Logotype size"
            options={getSizeOptions(logotypeType)}
            name="logotypeSize"
            value={qrCodeFormat}
            onChange={handleSizeChange}
            placeholder="Select"
            isClearable={false}
            required={!!logotypeType && !qrCodeFormat}
            disabled={!logotypeType}
          />
        </DialogContentContainer>
      </StyledDialog>
    </>
  );
};

const QRCodeButtonBox = styled.div`
  width: 100%;
  border-radius: 4px;
  border: none;
  display: flex;
  justify-content: start;
  align-items: center;
  max-height: 70px;
  margin-top: 10px;
  cursor: pointer;

  .p-button.p-button-text {
    padding: 0;
    background-color: transparent;
    border: none;
    ${(props) => props.theme.fonts.standard};
  }
  .p-button.p-button-text:hover {
    background-color: transparent;
  }
  .p-button.p-button-text:disabled {
    color: #757575;
  }
`;

const StyledDialog = styled(Dialog)`
  .p-dialog-header-close {
    display: none;
  }
  .p-dialog-footer {
    padding-top: 30px;
  }
  .p-dialog-footer .p-button {
    min-width: 150px;
  }
`;

const DialogContentContainer = styled.div`
  padding: 0.25rem 2rem 0 2rem;
`;

const InformationContainer = styled.div`
  background-color: ${(props) => props.theme.colors.elementsFilledInput};
  text-align: justify;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 27px;
`;

export default QRCodeDownloadDialog;

function getFileNameFromHeaders(headers: AxiosResponseHeaders): string | null {
  const contentDisposition = headers['content-disposition'];
  if (!contentDisposition) {
    // If this happens, double-check that Access-Control-Expose-Headers has this header
    return null;
  }
  // Regexp source: https://stackoverflow.com/a/23054920
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const match = contentDisposition.match(filenameRegex);
  if (!match) {
    return null;
  }
  const value = match[1];
  return value[0] === `"` || value[0] === `'` ? value.substring(1, value.length - 1) : value;
}
